/**
 * Manager tracking view (MANAGER-TRACKING-VIEW-V1, V1-P4) -- the J2/J3 landing
 * section that lands atop /dashboard/agm (surface fate 04-SURFACE-FATES row 37).
 *
 * TWO PARTS:
 *  - ManagerTrackingSection: PURE presentational component over a
 *    ManagerTrackingView prop (the render tests drive it directly -- no fetch).
 *  - ManagerTrackingPanel (default export): the self-fetching, fault-isolated
 *    container mounted on AgmDashboardPage (loading / error+Retry / data).
 *
 * THE VISUAL BAR (design-language 06 sec 1): tokens only (every colour is a
 * design-language status/urgency token); the six item-9 category colours are
 * CATEGORY identity carried by icon + label + number (never colour alone); the
 * matrix is the 04 sec 4 heat-cell grid (3-level urgency ramp, oldest-age
 * emphasis on the worst row, per-staff load bars); metric numbers are
 * tabular-nums display grades. Honest states: seeded empty copy, skeleton
 * loading, the S4 error block with Retry, honest-empty coming tiles.
 *
 * SEE-NOT-DO: this surface OBSERVES. It renders counts + drills only -- there
 * is no Claim / Assign / Qualify / stage affordance anywhere. Drills reach the
 * read-only command center; the canonical Lead-360 stays the only patient-level
 * terminus (reached from the command center, never a non-canonical link here).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import {
  AlertTriangle,
  CalendarCheck,
  CalendarX,
  Clock,
  Moon,
  Wallet,
} from 'lucide-react';
import { fetchManagerTracking } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import type { DashboardMe } from '@/dashboard/types';
import LeadQuickView from '@/dashboard/leads/LeadQuickView';
import { buildQuickViewFromTileItem } from '@/dashboard/leads/lead-quickview-view';
import { ChevronRight } from 'lucide-react';
import {
  categoryMeta,
  categoryScopeNote,
  formatManagerAge,
  loadBarPercent,
  maxStaffLoad,
  tileSubline,
  MANAGER_COMMAND_CENTER_HREF,
  type ManagerCategoryKey,
  type ManagerCategoryTileView,
  type ManagerClinicRowView,
  type ManagerStaffLoadView,
  type ManagerTrackingView,
  type ManagerUrgencyLevel,
  type ManagerTileItemView,
} from '@/dashboard/manager-tracking/manager-tracking-view';

// One lucide icon per category (06 no-new-libraries ruling: icons = lucide).
type IconType = typeof AlertTriangle;
const CATEGORY_ICON: Record<ManagerCategoryKey, IconType> = {
  untouched: AlertTriangle,
  followups_due: Clock,
  missed_appointments: CalendarX,
  pending_payments: Wallet,
  todays_appointments: CalendarCheck,
  no_activity: Moon,
};

/** The urgency companion icon (P4): warm = clock, breach = alert, calm = none. */
function UrgencyIcon({ level }: { level: ManagerUrgencyLevel }): JSX.Element | null {
  if (level === 'breach') {
    return <AlertTriangle className="murgency__icon" aria-hidden="true" size={13} />;
  }
  if (level === 'warm') {
    return <Clock className="murgency__icon" aria-hidden="true" size={13} />;
  }
  return null;
}

/** One item-9 category tile. REAL tiles drill; honest-empty tiles never link. */
function CategoryTile({
  tile,
  active,
  onSelect,
}: {
  tile: ManagerCategoryTileView;
  /** STITCH-2 SCOPE 6: the tile is an in-place filter (the leakage pattern). */
  active: boolean;
  onSelect: (key: ManagerCategoryKey) => void;
}): JSX.Element {
  const meta = categoryMeta(tile.key);
  const Icon = CATEGORY_ICON[tile.key];
  const testid = `mgr-tile-${tile.key}`;
  // Rider (b): the predicate-derived scope note as a help tooltip.
  const scopeNote = categoryScopeNote(tile.key);
  const head = (
    <span className="mtile__head">
      <Icon className="mtile__icon" aria-hidden="true" size={15} />
      <span className="mtile__label">{meta.label}</span>
    </span>
  );

  if (!tile.available) {
    // Honest-empty coming-state: no fabricated number, cite the owning package.
    return (
      <div
        className={`mtile mtile--coming mtile--${meta.tone}`}
        data-testid={testid}
        data-available="false"
        aria-disabled="true"
        title={scopeNote}
      >
        {head}
        <span className="mtile__value mtile__value--coming" aria-hidden="true">
          &mdash;
        </span>
        <span className="mtile__coming" data-testid={`mgr-tile-coming-${tile.key}`}>
          {/* MONEY-COMPLETION-V1: comingSource null on an unavailable tile
              = the source EXISTS but its read faulted -- say that, never
              "awaiting null" and never a fake zero. */}
          {tile.comingSource !== null
            ? `Awaiting ${tile.comingSource}`
            : 'Could not load right now'}
        </span>
      </div>
    );
  }

  const subline = tileSubline(tile);
  // STITCH-2 SCOPE 6 (the leakage pattern): clicking FILTERS the in-place
  // detail list below -- it never navigates away; "View command center"
  // stays the one full-page door.
  return (
    <button
      type="button"
      className={`mtile mtile--${meta.tone} mtile--u-${tile.urgency}${active ? ' mtile--active' : ''}`}
      data-testid={testid}
      data-available="true"
      data-urgency={tile.urgency}
      aria-pressed={active}
      title={scopeNote}
      onClick={() => onSelect(tile.key)}
    >
      {head}
      <span className="mtile__value" data-testid={`${testid}-count`}>
        {tile.count}
      </span>
      {subline.length > 0 ? (
        <span className={`mtile__age murgency murgency--${tile.urgency}`}>
          <UrgencyIcon level={tile.urgency} />
          {subline}
        </span>
      ) : null}
    </button>
  );
}

/** The in-place drill list under the tile strip (STITCH-2 SCOPE 6). */
function TileDrill({
  tile,
  clinicLabelById,
  onOpen,
}: {
  tile: ManagerCategoryTileView;
  clinicLabelById: ReadonlyMap<number, string>;
  onOpen: (item: ManagerTileItemView) => void;
}): JSX.Element {
  const meta = categoryMeta(tile.key);
  if (!tile.available) {
    return (
      <p className="mgr-drill__note" data-testid="mgr-drill-coming">
        {tile.comingSource !== null
          ? `${meta.label}: awaiting ${tile.comingSource}.`
          : `${meta.label}: could not load right now.`}
      </p>
    );
  }
  const items = tile.items ?? [];
  if (tile.key === 'todays_appointments') {
    return (
      <p className="mgr-drill__note" data-testid="mgr-drill-count-only">
        {tile.count} appointments are scheduled today. The day list lives on
        the clinic day view.
      </p>
    );
  }
  if (tile.key === 'pending_payments') {
    // MONEY-COMPLETION-V1: episode-grain money -- the row list (patient /
    // visit / accepted / collected / outstanding / age) lives on the
    // billing surface for the roles that hold it; this strip carries the
    // honest count + amount only (concept-specific evidence, never a
    // lead-queue drill).
    return (
      <p className="mgr-drill__note" data-testid="mgr-drill-count-only">
        {tile.count} closed {tile.count === 1 ? 'visit' : 'visits'} with
        accepted treatment not fully collected. The billing queue on the
        billing surface carries the rows.
      </p>
    );
  }
  if (tile.key === 'missed_appointments') {
    // MISSED-APPOINTMENT-RECOVERY-V1: appointment-grain recovery work -- the
    // row list (who / when / clinic / next step) lives on the COC no-show
    // recovery desk for the clinic-scoped operator; this manager strip carries
    // the honest count only (concept-specific evidence, never a lead-queue
    // drill).
    return (
      <p className="mgr-drill__note" data-testid="mgr-drill-count-only">
        {tile.count} missed {tile.count === 1 ? 'appointment' : 'appointments'} to
        recover. The COC no-show recovery desk carries the rows.
      </p>
    );
  }
  if (items.length === 0) {
    return (
      <p className="mgr-drill__note" data-testid="mgr-drill-empty">
        Nothing in {meta.label.toLowerCase()} right now.
      </p>
    );
  }
  return (
    <div className="sla-list" data-testid="mgr-drill-list">
      {items.map((item) => (
        <button
          key={item.safeLeadId}
          type="button"
          className="sla-row sla-row--neutral"
          data-testid="mgr-drill-row"
          onClick={() => onOpen(item)}
        >
          <span className="sla-row__main">
            <span className="sla-row__name">
              {item.displayName.trim() !== '' ? item.displayName : 'Unnamed lead'}
            </span>
            <span className="sla-row__meta">
              <span className="sla-row__clinic">
                {clinicLabelOf(clinicLabelById.get(item.clinicId))}
              </span>
              {item.assigned === false ? (
                <span className="ac-badge ac-badge--pool">Unclaimed</span>
              ) : item.ownerLabel !== null ? (
                <span className="sla-row__owner">{item.ownerLabel}</span>
              ) : null}
            </span>
          </span>
          <span className="sla-row__right">
            <span className="sla-row__age">
              {item.ageMinutes !== null ? formatManagerAge(item.ageMinutes) : '-'}
            </span>
            <ChevronRight className="sla-row__chev" aria-hidden="true" size={16} />
          </span>
        </button>
      ))}
      {tile.count > items.length ? (
        <p className="mgr-drill__note" data-testid="mgr-drill-capped">
          Showing the oldest {items.length} of {tile.count}. Open the command
          center for the full queue.
        </p>
      ) : null}
    </div>
  );
}

/** The clinic x metric heat-cell grid (04 sec 4). Worst row gets the emphasis. */
function ClinicMatrix({
  clinics,
  worstClinicId,
}: {
  clinics: readonly ManagerClinicRowView[];
  worstClinicId: number | null;
}): JSX.Element {
  if (clinics.length === 0) {
    return (
      <p className="mmatrix__empty" data-testid="mgr-matrix-empty">
        No open intake leads in scope right now.
      </p>
    );
  }
  return (
    <div className="mmatrix__scroll">
      <table className="mmatrix" data-testid="mgr-matrix">
        <thead>
          <tr>
            <th scope="col">Clinic</th>
            <th scope="col" className="mmatrix__num">Depth</th>
            <th scope="col" className="mmatrix__num">Untouched</th>
            <th scope="col" className="mmatrix__num">Oldest</th>
            <th scope="col" className="mmatrix__num">Breach</th>
          </tr>
        </thead>
        <tbody>
          {clinics.map((c) => {
            const worst = c.clinicId === worstClinicId;
            return (
              <tr
                key={c.clinicId}
                className={`mrow mrow--${c.urgency}${worst ? ' mrow--worst' : ''}`}
                data-testid="mgr-matrix-row"
                data-clinic={c.clinicId}
              >
                <td className="mrow__label">
                  <a href={MANAGER_COMMAND_CENTER_HREF} data-testid="mgr-matrix-drill">
                    {c.clinicLabel}
                  </a>
                </td>
                <td className="mrow__num">{c.depth}</td>
                <td className="mrow__num">{c.untouched === null ? '\u2014' : c.untouched}</td>
                <td className={`mrow__num mcell mcell--${c.urgency}`}>
                  <span className="mcell__inner">
                    <UrgencyIcon level={c.urgency} />
                    {formatManagerAge(c.oldestAgeMinutes)}
                  </span>
                </td>
                <td className={`mrow__num${c.breach > 0 ? ' mcell mcell--breach' : ' mrow__num--muted'}`}>
                  {c.breach}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Per-staff load bars (04 sec 4): "who is overloaded". Display-only. */
function StaffLoad({ staff }: { staff: readonly ManagerStaffLoadView[] }): JSX.Element {
  if (staff.length === 0) {
    return (
      <p className="mstaff__empty" data-testid="mgr-staff-empty">
        No assigned load in scope.
      </p>
    );
  }
  const max = maxStaffLoad(staff);
  return (
    <div className="mstaff" data-testid="mgr-staff">
      {staff.map((s) => (
        <div
          key={s.ownerLabel}
          className={`mstaff__row${s.isPool ? ' mstaff__row--pool' : ''}`}
          data-testid="mgr-staff-row"
        >
          <span className="mstaff__label">
            {s.isPool ? 'Pool (unassigned)' : s.ownerLabel}
          </span>
          <span className="mstaff__track" aria-hidden="true">
            <span
              className="mstaff__bar"
              style={{ width: `${loadBarPercent(s.load, max)}%` }}
            />
          </span>
          <span className="mstaff__count">{s.load}</span>
        </div>
      ))}
    </div>
  );
}

/** PURE presentational section over a resolved ManagerTrackingView. */
export function ManagerTrackingSection({
  data,
  me = null,
  onChanged,
}: {
  data: ManagerTrackingView;
  /** The caller's /me payload -- drives the drawer's action strip. */
  me?: DashboardMe | null;
  /** Refetch after a successful owner change in the drawer (adversarial
   *  review fix: stale tile counts after an assign). */
  onChanged?: () => void;
}): JSX.Element {
  // STITCH-2 SCOPE 6: tile selection filters the in-place drill below
  // (default = the first tile with items); rows open the ONE LeadQuickView.
  const defaultKey = useMemo<ManagerCategoryKey>(() => {
    const withItems = data.categories.find((t) => (t.items?.length ?? 0) > 0);
    return withItems?.key ?? data.categories[0]?.key ?? 'untouched';
  }, [data]);
  const [selected, setSelected] = useState<ManagerCategoryKey>(defaultKey);
  const [openItem, setOpenItem] = useState<ManagerTileItemView | null>(null);
  const selectedTile =
    data.categories.find((t) => t.key === selected) ?? data.categories[0];
  // Clinic labels for drill rows come from the matrix (already on the wire).
  const clinicLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of data.matrix.clinics) map.set(row.clinicId, row.clinicLabel);
    return map;
  }, [data]);
  return (
    <section className="mgr-tracking" data-testid="mgr-tracking" aria-label="Ops tracking">
      <header className="mgr-tracking__head">
        <div>
          <span className="mgr-tracking__eyebrow">Ops tracking</span>
          <h2 className="mgr-tracking__title">Queue health</h2>
        </div>
        <div className="mgr-tracking__glance">
          <span className="mgr-tracking__glance-num" data-testid="mgr-total-breaches">
            {data.totalBreaches}
          </span>
          {/* E8 (LEAD-APPOINTMENT-CRM-COHERENCE-V1 SCOPE 6): the queue-health
              breach count ACROSS the manager's clinics -- labelled distinctly
              from the leakage-signals glance so the two numbers no longer share
              the bare "SLA breaches" name. */}
          <span className="mgr-tracking__glance-label">Clinic SLA breaches</span>
        </div>
      </header>

      {!data.matrix.ownershipAvailable ? (
        <p className="mgr-tracking__note" data-testid="mgr-ownership-note">
          Assignment overlay off &mdash; unassigned and breach are not derivable; open
          totals shown.
        </p>
      ) : null}

      <div className="mgr-strip" data-testid="mgr-strip">
        {data.categories.map((t) => (
          <CategoryTile
            key={t.key}
            tile={t}
            active={t.key === selected}
            onSelect={setSelected}
          />
        ))}
      </div>

      {selectedTile !== undefined ? (
        <div className="mgr-drill" data-testid="mgr-drill">
          <div className="sla-list-block__head">
            <span className="sla-list-block__title">
              {categoryMeta(selectedTile.key).label}
            </span>
            <span className="sla-list-block__hint">
              {categoryScopeNote(selectedTile.key)}
            </span>
          </div>
          <TileDrill
            tile={selectedTile}
            clinicLabelById={clinicLabelById}
            onOpen={setOpenItem}
          />
        </div>
      ) : null}

      {openItem !== null ? (
        <LeadQuickView
          model={buildQuickViewFromTileItem({
            ...openItem,
            clinicLabel:
              clinicLabelOf(clinicLabelById.get(openItem.clinicId)),
          })}
          me={me}
          onClose={() => setOpenItem(null)}
          onChanged={() => {
            setOpenItem(null);
            onChanged?.();
          }}
        />
      ) : null}

      <div className="mgr-tracking__grid">
        <div className="mgr-tracking__block">
          <span className="mgr-tracking__block-label">Clinic &times; metric</span>
          <ClinicMatrix
            clinics={data.matrix.clinics}
            worstClinicId={data.matrix.worstClinicId}
          />
        </div>
        <div className="mgr-tracking__block">
          <span className="mgr-tracking__block-label">Workload by desk</span>
          <StaffLoad staff={data.matrix.staff} />
        </div>
      </div>

      <a
        className="mgr-tracking__drill"
        href={MANAGER_COMMAND_CENTER_HREF}
        data-testid="mgr-open-command-center"
      >
        View command center &rarr;
      </a>
    </section>
  );
}

/** The self-fetching, fault-isolated panel mounted on AgmDashboardPage. */
export default function ManagerTrackingPanel(): JSX.Element {
  const meCtx = useDashboardMeContext();
  const [data, setData] = useState<ManagerTrackingView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchManagerTracking()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  if (error) {
    return (
      <section className="mgr-tracking mgr-tracking--error" data-testid="mgr-error">
        <p className="mgr-tracking__errline">Could not load ops tracking. {error}</p>
        <button type="button" className="ac-btn ac-btn--secondary" onClick={() => load()}>
          Retry
        </button>
      </section>
    );
  }

  if (!data) {
    return (
      <section
        className="mgr-tracking mgr-tracking--loading"
        data-testid="mgr-loading"
        aria-busy={loading}
      >
        <span className="mgr-tracking__eyebrow">Ops tracking</span>
        <div className="mgr-strip mgr-strip--skel" aria-hidden="true">
          <span className="mtile mtile--skel" />
          <span className="mtile mtile--skel" />
          <span className="mtile mtile--skel" />
        </div>
        <p className="mgr-tracking__loadline">Loading ops tracking&hellip;</p>
      </section>
    );
  }

  return (
    <ManagerTrackingSection
      data={data}
      me={meCtx?.me ?? null}
      onChanged={() => load()}
    />
  );
}
